/**
 * Przełączniki retuszu narzędzia „Sprzątanie i retusz" — agent decyduje, CO poprawiamy.
 *
 * Mieszkają w pakiecie współdzielonym razem z profilami światła i z tego samego
 * powodu: identyfikator przechodzi przez granicę HTTP (front wysyła listę, API
 * dobiera po niej moduły promptu). Dwie osobne listy literałów rozjechałyby się
 * przy pierwszym dołożonym przełączniku, a użytkownik zobaczyłby to dopiero na
 * wyniku — jako poprawkę, o którą nie prosił, albo brak tej, o którą prosił.
 *
 * Czego tu NIE MA i mieć nie będzie: „dodaj meble" i „zmień wykończenie". Zakaz
 * dostawiania czegokolwiek i przebudowy wnętrza jest niezmiennikiem tego narzędzia,
 * a nie jego opcją — gdyby dało się go wyłączyć, narzędzie przestałoby się różnić
 * od Home Stagingu i od Wirtualnego remontu, które tę pracę wykonują świadomie
 * i mają na nią własne ekrany.
 */

export type RetouchOption =
  | "perspective"
  | "lighting"
  | "clutter"
  | "reflections"
  | "color"
  | "anonymize";

/** Kolejność = kolejność w interfejsie, od najczęściej potrzebnej poprawki. */
export const RETOUCH_OPTIONS: readonly [RetouchOption, ...RetouchOption[]] = [
  "perspective",
  "lighting",
  "clutter",
  "reflections",
  "color",
  "anonymize",
];

/**
 * Domyślnie WSZYSTKO włączone — agent, który nic nie rusza, dostaje pełny retusz.
 * Odwrotna domyślność (nic nie zaznaczone) zamieniałaby wgranie zdjęcia w ankietę.
 */
export const DEFAULT_RETOUCH_OPTIONS: readonly RetouchOption[] = RETOUCH_OPTIONS;

export function isRetouchOption(value: string): value is RetouchOption {
  return (RETOUCH_OPTIONS as readonly string[]).includes(value);
}

export interface RetouchOptionDefinition {
  id: RetouchOption;
  /** Etykieta przełącznika. */
  name: string;
  /** Jedno zdanie „co się stanie" — podpis pod etykietą. */
  desc: string;
}

export const RETOUCH_OPTION_DEFINITIONS: Readonly<Record<RetouchOption, RetouchOptionDefinition>> = {
  perspective: {
    id: "perspective",
    name: "Wyprostuj pion i kadr",
    desc: "ściany i futryny w pionie, wypoziomowany horyzont, bez beczki obiektywu",
  },
  lighting: {
    id: "lighting",
    name: "Wyrównaj światło",
    desc: "doświetlone ciemne kąty, okna bez prześwietleń, neutralna biel",
  },
  clutter: {
    id: "clutter",
    name: "Usuń bałagan",
    desc: "kable, butelki, naczynia, porozrzucane pranie — nic poza tym",
  },
  reflections: {
    id: "reflections",
    name: "Usuń odbicia i refleksy",
    desc: "szyby, lustra, błyszczące blaty i fronty bez odblasków",
  },
  color: {
    id: "color",
    name: "Podkręć kolory i detal",
    desc: "naturalne nasycenie, mikrokontrast, wyraźna faktura materiałów",
  },
  anonymize: {
    id: "anonymize",
    name: "Zanonimizuj twarze",
    desc: "portrety rodzinne zamienione na neutralną grafikę w tej samej ramie",
  },
};

export const RETOUCH_OPTION_LIST: readonly RetouchOptionDefinition[] = RETOUCH_OPTIONS.map(
  (id) => RETOUCH_OPTION_DEFINITIONS[id],
);
